import {useFormik} from 'formik'
import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import * as Yup from 'yup'
import {toAbsoluteUrl} from '../../../../_gori/helpers'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {Button} from '../../../../_gori/partials/widgets'
import AgreementService from '../../agreements/core/_requests'
import {useAuth} from '../../auth'

type Props = {
  show: boolean
  handleClose: () => void
}

// USPS reference links (official terminology — kept untranslated as constants)
const USPS = {
  PUB_52: 'https://pe.usps.com/text/pub52/welcome.htm',
  HAZMAT_TOOL: 'https://pe.usps.com/hazmat/index',
  PI_9D: 'https://pe.usps.com/text/pub52/pub52apxc_032.htm',
  PI_9E: 'https://pe.usps.com/text/pub52/pub52apxc_033.htm',
  SHIPPING_RESTRICTIONS: 'https://usps.com/ship/shipping-restrictions.htm',
  PI_3A: 'https://pe.usps.com/text/pub52/pub52apxc_009.htm',
  PI_8A: 'https://pe.usps.com/text/pub52/pub52apxc_026.htm',
  PI_8B: 'https://pe.usps.com/text/pub52/pub52apxc_027.htm',
  PI_8C: 'https://pe.usps.com/text/pub52/pub52apxc_028.htm',
  PI_9A: 'https://pe.usps.com/text/pub52/pub52apxc_029.htm',
  PI_6C: 'https://pe.usps.com/text/pub52/pub52apxc_019.htm',
}

const PROHIBITED_ICONS = ['battery', 'spray', 'nail', 'thermometer', 'paint', 'liquor']

type RestrictedRow = {key: string; links: {label: string; href: string}[]}
const RESTRICTED_ROWS: RestrictedRow[] = [
  {
    key: 'R1',
    links: [
      {label: 'PI 9D (Domestic)', href: USPS.PI_9D},
      {label: 'PI 9E (International/APO/FPO/DPO)', href: USPS.PI_9E},
    ],
  },
  {key: 'R2', links: [{label: 'USPS Pub 52', href: USPS.PUB_52}]},
  {key: 'R3', links: [{label: 'Shipping Restrictions', href: USPS.SHIPPING_RESTRICTIONS}]},
  {key: 'R4', links: [{label: 'PI 3A', href: USPS.PI_3A}]},
  {
    key: 'R5',
    links: [
      {label: 'PI 8A', href: USPS.PI_8A},
      {label: 'PI 8B', href: USPS.PI_8B},
      {label: 'PI 8C', href: USPS.PI_8C},
    ],
  },
  {key: 'R6', links: [{label: 'PI 9A', href: USPS.PI_9A}]},
  {key: 'R7', links: [{label: 'PI 6C', href: USPS.PI_6C}]},
]

const HazmatModal: React.FC<Props> = ({show, handleClose}) => {
  const intl = useIntl()
  const {authYup} = UseYupValidate()
  const {currentUser} = useAuth()
  const {isCancel, newCancelToken} = useCancelToken()
  const [loading, setLoading] = useState(false)
  const [hideFor48Hours, setHideFor48Hours] = useState(false)
  const accepted = !!currentUser?.remind_at

  const formik = useFormik({
    initialValues: {accept_terms: accepted},
    validationSchema: Yup.object().shape({accept_terms: authYup.acceptTerms}),
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (loading) return
      try {
        setLoading(true)
        const res = await AgreementService.updateHazmat(
          accepted ? {hazmat: true, show_reminder: !hideFor48Hours} : {hazmat: values.accept_terms},
          {cancelToken: newCancelToken()}
        )
        if (res) handleClose()
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setLoading(false)
      }
    },
  })

  const t = (id: string) => intl.formatMessage({id})
  const link = (label: string, href: string) => (
    <a href={href} target='_blank' rel='noreferrer' className='hazmat-modal__link'>
      {label}
    </a>
  )

  return (
    <Modal
      className='hazmat-modal'
      dialogClassName='hazmat-modal__dialog'
      contentClassName='hazmat-modal__content'
      show={show}
      backdrop='static'
      centered
      keyboard={accepted}
      onHide={handleClose}
    >
      <Modal.Header className='hazmat-modal__header' closeButton={accepted}>
        <Modal.Title bsPrefix='hazmat-modal__title'>{t('HAZMAT_POLICY_TITLE')}</Modal.Title>
      </Modal.Header>

      <Modal.Body className='hazmat-modal__body'>
        <div className='hazmat-modal__scroll'>
          <>
            <div className='hazmat-modal__intro'>
              <p className='hazmat-modal__intro-lead'>{t('HAZMAT_POLICY_INTRO_1')}</p>
              <p className='hazmat-modal__text'>{t('HAZMAT_POLICY_INTRO_2')}</p>
              <p className='hazmat-modal__text'>{t('HAZMAT_POLICY_INTRO_3')}</p>
            </div>

            <div className='hazmat-modal__fee'>
              <span className='hazmat-modal__badge'>{t('HAZMAT_POLICY_PENALTY_BADGE')}</span>
              <p className='hazmat-modal__fee-title'>{t('HAZMAT_POLICY_PENALTY_TITLE')}</p>
              <p className='hazmat-modal__fee-desc'>{t('HAZMAT_POLICY_PENALTY_DESC')}</p>
              <ul className='hazmat-modal__fee-list'>
                <li>
                  <strong>{t('HAZMAT_POLICY_PENALTY_ITEM_1_LABEL')}</strong> |{' '}
                  {t('HAZMAT_POLICY_PENALTY_ITEM_1_VALUE')}
                </li>
                <li>
                  <strong>{t('HAZMAT_POLICY_PENALTY_ITEM_2_LABEL')}</strong> |{' '}
                  {t('HAZMAT_POLICY_PENALTY_ITEM_2_VALUE')}
                </li>
              </ul>
            </div>

            <div className='hazmat-modal__fee'>
              <span className='hazmat-modal__badge'>{t('HAZMAT_POLICY_HANDLING_BADGE')}</span>
              <p className='hazmat-modal__fee-title'>{t('HAZMAT_POLICY_HANDLING_TITLE')}</p>
              <p className='hazmat-modal__fee-desc'>
                {t('HAZMAT_POLICY_HANDLING_PM_LABEL')}{' '}
                <strong>{t('HAZMAT_POLICY_HANDLING_PM_VALUE')}</strong>
              </p>
              <p className='hazmat-modal__fee-note'>{t('HAZMAT_POLICY_HANDLING_DESC')}</p>
            </div>

            <div className='hazmat-modal__divider' />

            <div className='hazmat-modal__section'>
              <p className='hazmat-modal__section-title hazmat-modal__section-title--danger'>
                {t('HAZMAT_POLICY_PROHIBITED_TITLE')}
              </p>
              <p className='hazmat-modal__text'>
                <FormattedMessage
                  id='HAZMAT_POLICY_PROHIBITED_DESC'
                  values={{b: (chunks: React.ReactNode) => <strong>{chunks}</strong>}}
                />
              </p>
              <div className='hazmat-modal__icons'>
                {PROHIBITED_ICONS.map((icon) => (
                  <img
                    key={icon}
                    src={toAbsoluteUrl(`/media/gori/dashboard/hazmat/${icon}.png`)}
                    alt=''
                  />
                ))}
              </div>
              <ul className='hazmat-modal__list'>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <li key={n}>{t(`HAZMAT_POLICY_PROHIBITED_${n}`)}</li>
                ))}
              </ul>
              <p className='hazmat-modal__text'>{t('HAZMAT_POLICY_PROHIBITED_PENALTY')}</p>
              <p className='hazmat-modal__text'>
                {t('HAZMAT_POLICY_FULL_GUIDELINES')} {link('USPS Publication 52', USPS.PUB_52)}
              </p>
            </div>

            <div className='hazmat-modal__divider' />

            <div className='hazmat-modal__section'>
              <p className='hazmat-modal__section-title hazmat-modal__section-title--teal'>
                {t('HAZMAT_POLICY_RESTRICTED_TITLE')}
              </p>
              <p className='hazmat-modal__text'>{t('HAZMAT_POLICY_RESTRICTED_DESC')}</p>
              <div className='hazmat-modal__table'>
                <div className='hazmat-modal__row hazmat-modal__row--head'>
                  <div className='hazmat-modal__cell hazmat-modal__cell--cat'>
                    {t('HAZMAT_POLICY_TABLE_CATEGORY')}
                  </div>
                  <div className='hazmat-modal__cell'>{t('HAZMAT_POLICY_TABLE_CONDITIONS')}</div>
                </div>
                {RESTRICTED_ROWS.map((row) => (
                  <div className='hazmat-modal__row' key={row.key}>
                    <div className='hazmat-modal__cell hazmat-modal__cell--cat'>
                      <span className='hazmat-modal__cell-name'>
                        {t(`HAZMAT_POLICY_${row.key}_NAME`)}
                      </span>
                      <span className='hazmat-modal__cell-sub'>
                        {t(`HAZMAT_POLICY_${row.key}_SUB`)}
                      </span>
                    </div>
                    <div className='hazmat-modal__cell'>
                      <span className='hazmat-modal__cell-cond'>
                        {t(`HAZMAT_POLICY_${row.key}_COND`)}
                      </span>
                      <span className='hazmat-modal__cell-links'>
                        {row.links.map((l, idx) => (
                          <React.Fragment key={l.href}>
                            {idx > 0 && <span className='hazmat-modal__pipe'>|</span>}
                            {link(l.label, l.href)}
                          </React.Fragment>
                        ))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className='hazmat-modal__text'>{t('HAZMAT_POLICY_RESTRICTED_DECLARE')}</p>
              <p className='hazmat-modal__text'>
                {t('HAZMAT_POLICY_FULL_GUIDELINES')} {link('USPS Publication 52', USPS.PUB_52)} |{' '}
                {link('HazMat Items Search Tool', USPS.HAZMAT_TOOL)}
              </p>
            </div>
          </>
        </div>

        {accepted && (
          <div className='hazmat-modal__agreed'>
            <p className='hazmat-modal__statement'>{t('HAZMAT_POST_STATEMENT')}</p>
          </div>
        )}

        {!accepted && (
          <div className='hazmat-modal__accept'>
            <div className='hazmat-modal__accept-title'>
              <span className='required'>{t('ACCEPT_TERMS')}</span>
            </div>
            <div className='hazmat-modal__accept-body'>
              <input
                type='checkbox'
                name='accept_terms'
                className='form-check-input cursor-pointer hazmat-modal__checkbox'
                checked={formik.values.accept_terms}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div>
                <p className='hazmat-modal__accept-intro'>{t('HAZMAT_POLICY_AGREE_INTRO')}</p>
                <ul className='hazmat-modal__accept-list'>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <li key={n}>{t(`HAZMAT_POLICY_AGREE_${n}`)}</li>
                  ))}
                </ul>
              </div>
            </div>
            {formik.errors.accept_terms && formik.touched.accept_terms && (
              <p className='mt-1 text-danger'>{t('YOU_MUST_ACCEPT_THE_TERMS_AND_CONDITIONS')}</p>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className='hazmat-modal__footer'>
        {accepted && (
          <label className='hazmat-modal__hide'>
            <input
              type='checkbox'
              className='form-check-input cursor-pointer'
              checked={hideFor48Hours}
              onChange={(e) => setHideFor48Hours(e.target.checked)}
            />
            <span>{t('HIDE_FOR_48_HOURS')}</span>
          </label>
        )}
        <Button
          className='btn btn-advanced-primary hazmat-modal__submit'
          loading={loading}
          label={t('SUBMIT')}
          loadingText={t('SUBMIT')}
          event={() => formik.handleSubmit()}
          disabled={(!accepted && !formik.values.accept_terms) || loading}
        />
      </Modal.Footer>
    </Modal>
  )
}

export {HazmatModal}
